import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import QuestionBasicSettings from "./questions/QuestionBasicSettings";
import QuestionTranslations from "./questions/QuestionTranslations";
import QuestionOptionsPreview from "./questions/QuestionOptionsPreview";
import FocusAreaSelector from "./questions/FocusAreaSelector";
import SurveyTemplateLinks from "./questions/SurveyTemplateLinks";
import QuestionAdvancedSettings from "./questions/QuestionAdvancedSettings";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { DEFAULT_LANG } from "@/i18n";
import { useLanguages } from "@/hooks/useLanguages";

interface Question {
  id: string;
  question_text: string;
  question_type_id: string;
  is_required: boolean;
  is_reverse_scored: boolean;
  order_index: number;
  tool_id: string;
  options?: any;
  question_focus_areas?: Array<{
    focus_areas: {
      id: string;
      name: string;
    };
  }>;
}

interface QuestionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  question: Question | null;
  onSuccess: () => void;
}

export const QuestionDialog = ({ isOpen, onClose, question, onSuccess }: QuestionDialogProps) => {
  const [formData, setFormData] = useState({
    question_text: "",
    question_type_id: "",
    tool_id: "",
    is_required: true,
    is_reverse_scored: false,
    order_index: 1,
    options: null as any,
  });
  const [selectedFocusAreas, setSelectedFocusAreas] = useState<string[]>([]);
  const { i18n } = useTranslation();
  const { data: languages } = useLanguages();
  const [activeLang, setActiveLang] = useState<string>(i18n.language || DEFAULT_LANG);
  const [questionTextI18n, setQuestionTextI18n] = useState<Record<string, string>>({});

  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);
  const [templateSearch, setTemplateSearch] = useState("");
  const [focusAreaSearch, setFocusAreaSearch] = useState("");

  // Fetch tools
  const { data: tools } = useQuery({
    queryKey: ['tools'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tools')
        .select('id, name')
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data;
    },
  });

  // Fetch focus areas
  const { data: focusAreas } = useQuery({
    queryKey: ['focus-areas'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('focus_areas')
        .select('id, name')
        .order('name');
      if (error) throw error;
      return data;
    },
  });

  // Fetch question types
  const { data: questionTypes } = useQuery({
    queryKey: ['question-types'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('question_types')
        .select('id, name, options, options_i18n')
        .order('name');
      if (error) throw error;
      return data;
    },
  });

  // Fetch survey templates for linking
  const { data: templates } = useQuery({
    queryKey: ['survey-templates-for-question-dialog'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('survey_templates')
        .select('id, name')
        .order('name');
      if (error) throw error;
      return data as { id: string; name: string }[];
    },
  });

  // Fetch existing template links for this question
  const { data: templateLinks } = useQuery({
    queryKey: ['question-template-links', question?.id],
    enabled: !!question?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('survey_template_questions')
        .select('template_id')
        .eq('question_id', (question as any).id);
      if (error) throw error;
      return data as { template_id: string }[];
    },
  });

  useEffect(() => {
    if (question) {
      setFormData({
        question_text: question.question_text,
        question_type_id: question.question_type_id,
        tool_id: (question.tool_id as any) || "",
        is_required: question.is_required,
        is_reverse_scored: question.is_reverse_scored,
        order_index: question.order_index,
        options: question.options,
      });

      if (question.question_focus_areas) {
        setSelectedFocusAreas(
          question.question_focus_areas.map(qfa => qfa.focus_areas.id)
        );
      }

      // Options are now managed at the question type level

      const q: any = question as any;
      // Properly parse JSONB i18n field
      let parsedI18n = {};
      if (q.question_text_i18n) {
        if (typeof q.question_text_i18n === 'string') {
          try {
            parsedI18n = JSON.parse(q.question_text_i18n);
          } catch {
            parsedI18n = {};
          }
        } else if (typeof q.question_text_i18n === 'object') {
          parsedI18n = q.question_text_i18n;
        }
      }
      setQuestionTextI18n(parsedI18n);
    } else {
      // Reset form for new question
      setFormData({
        question_text: "",
        question_type_id: "",
        tool_id: "",
        is_required: true,
        is_reverse_scored: false,
        order_index: 1,
        options: null,
      });
      setSelectedFocusAreas([]);
      setQuestionTextI18n({});
    }
   }, [question, isOpen]);
  useEffect(() => {
    if (question && templateLinks) {
      setSelectedTemplateIds(templateLinks.map((l) => l.template_id));
    }
    if (!question) {
      setSelectedTemplateIds([]);
    }
  }, [question, templateLinks]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const questionData: any = {
        ...formData,
        options: null,
        question_text_i18n: questionTextI18n,
      };
      // Ensure optional tool_id is null when not set (DB accepts NULL)
      questionData.tool_id = formData.tool_id ? formData.tool_id : null;

      let questionId: string;

      if (question) {
        // Update existing question
        const { error } = await supabase
          .from('questions')
          .update(questionData)
          .eq('id', question.id);

        if (error) throw error;
        questionId = question.id;
      } else {
        // Create new question
        const { data, error } = await supabase
          .from('questions')
          .insert(questionData)
          .select('id')
          .single();

        if (error) throw error;
        questionId = data.id;
      }

      // Update focus area relationships
      // Clear existing and insert fresh set when any selected
      await supabase.from('question_focus_areas').delete().eq('question_id', questionId);
      if (selectedFocusAreas.length > 0) {
        const relationships = selectedFocusAreas.map((focusAreaId) => ({
          question_id: questionId,
          focus_area_id: focusAreaId,
        }));
        const { error: relationError } = await supabase
          .from('question_focus_areas')
          .insert(relationships);
        if (relationError) throw relationError;
      }

      // Sync survey template links without disturbing existing order for kept links
      // Fetch current links if not already loaded
      let currentLinkIds: string[] = [];
      if (question?.id && templateLinks) {
        currentLinkIds = templateLinks.map((l) => l.template_id);
      } else {
        const { data: linkRows } = await supabase
          .from('survey_template_questions')
          .select('template_id')
          .eq('question_id', questionId);
        currentLinkIds = (linkRows || []).map((r: any) => r.template_id);
      }

      const toRemove = currentLinkIds.filter((id) => !selectedTemplateIds.includes(id));
      const toAdd = selectedTemplateIds.filter((id) => !currentLinkIds.includes(id));

      if (toRemove.length > 0) {
        await supabase
          .from('survey_template_questions')
          .delete()
          .eq('question_id', questionId)
          .in('template_id', toRemove);
      }

      if (toAdd.length > 0) {
        // For each added template, append at the end (max order_index + 1)
        await Promise.all(
          toAdd.map(async (templateId) => {
            const { data: maxRow } = await supabase
              .from('survey_template_questions')
              .select('order_index')
              .eq('template_id', templateId)
              .order('order_index', { ascending: false })
              .limit(1)
              .maybeSingle();
            const nextOrder = ((maxRow as any)?.order_index || 0) + 1;
            const { error: insertErr } = await supabase
              .from('survey_template_questions')
              .insert({ template_id: templateId, question_id: questionId, order_index: nextOrder });
            if (insertErr) throw insertErr;
          })
        );
      }

      toast.success(question ? 'Question updated successfully' : 'Question created successfully');
      onSuccess();
    } catch (error) {
      console.error('Error saving question:', error);
      toast.error('Failed to save question');
    }
  };

  const handleFocusAreaChange = (focusAreaId: string, checked: boolean) => {
    if (checked) {
      setSelectedFocusAreas(prev => [...prev, focusAreaId]);
    } else {
      setSelectedFocusAreas(prev => prev.filter(id => id !== focusAreaId));
    }
  };

  const handleTemplateToggle = (templateId: string, checked: boolean) => {
    setSelectedTemplateIds((prev) =>
      checked ? [...prev, templateId] : prev.filter((id) => id !== templateId)
    );
  };

  const selectedType = (questionTypes || []).find((t: any) => t.id === formData.question_type_id);
  const resolvedOptions = selectedType
    ? (Array.isArray((selectedType as any).options_i18n?.[activeLang])
        ? (selectedType as any).options_i18n[activeLang]
        : (Array.isArray((selectedType as any).options) ? (selectedType as any).options : []))
    : [] as string[];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {question ? "Edit Question" : "Add New Question"}
          </DialogTitle>
          <DialogDescription>
            {question ? "Update the question details below." : "Create a new question for the survey system."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <QuestionBasicSettings
            tools={tools as any}
            questionTypes={questionTypes as any}
            toolId={formData.tool_id}
            onToolChange={(value) => setFormData(prev => ({ ...prev, tool_id: value }))}
            questionTypeId={formData.question_type_id}
            onQuestionTypeChange={(value) => setFormData(prev => ({ ...prev, question_type_id: value }))}
            questionText={formData.question_text}
            onQuestionTextChange={(value) => setFormData(prev => ({ ...prev, question_text: value }))}
          />

          <QuestionTranslations
            languages={languages as any}
            activeLang={activeLang}
            onActiveLangChange={(v) => setActiveLang(v)}
            questionTextI18n={questionTextI18n}
            onI18nChange={(lang, value) => setQuestionTextI18n({ ...questionTextI18n, [lang]: value })}
          />

          <QuestionOptionsPreview options={resolvedOptions as string[]} />

          <FocusAreaSelector
            focusAreas={focusAreas as any}
            selectedFocusAreas={selectedFocusAreas}
            search={focusAreaSearch}
            onSearchChange={(v) => setFocusAreaSearch(v)}
            onToggle={handleFocusAreaChange}
          />

          <SurveyTemplateLinks
            templates={templates as any}
            selectedTemplateIds={selectedTemplateIds}
            search={templateSearch}
            onSearchChange={(v) => setTemplateSearch(v)}
            onToggle={handleTemplateToggle}
          />

          <QuestionAdvancedSettings
            orderIndex={formData.order_index}
            onOrderIndexChange={(value) => setFormData(prev => ({ ...prev, order_index: value }))}
            isRequired={formData.is_required}
            onRequiredChange={(checked) => setFormData(prev => ({ ...prev, is_required: checked }))}
            isReverseScored={formData.is_reverse_scored}
            onReverseScoredChange={(checked) => setFormData(prev => ({ ...prev, is_reverse_scored: checked }))}
          />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              {question ? "Update Question" : "Create Question"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};